import React, { ForwardedRef, forwardRef } from "react";
import {
  Select as BaseSelect,
  SelectOverrides,
  SelectProps as BaseSelectProps,
  SIZE,
  StyledDropdownListItem,
} from "baseui/select";
import styled from "styled-components";
import { CarbonIconProps, ChevronDown, Search } from "@carbon/icons-react";
import { Overrides } from "baseui/overrides";
import baseuiTheme, { Theme } from "../../../../styling/baseui_theme";
import InputClearIcon from "../shared/input_clear_icon";
import { mergeOverrides } from "baseui";
export * from "baseui/select";

export interface SelectProps extends BaseSelectProps {
  dataTest?: string;
  negativeGroupKeys?: string[];
  dataAnalyticsId?: string;
}

export const StyledSelectIcon = styled(ChevronDown)`
  cursor: pointer;
`;

const StyledSearchIcon = styled.div`
  display: flex;
  align-items: center;
  position: absolute;
  height: 100%;
  cursor: pointer;
  padding-left: 10px;

  svg {
    position: relative;
    top: -1px;
  }
`;

const NegativeStyledDropdownListItem = styled(StyledDropdownListItem)`
  color: ${(props) => props.theme.colors.negative500};
`;

export const getIconSize = ($size: BaseSelectProps["size"]) => {
  let size = baseuiTheme.typography.font350.lineHeight;
  switch ($size) {
    case SIZE.mini:
      size = baseuiTheme.typography.font150.lineHeight as string;
      break;
    case SIZE.compact:
      size = baseuiTheme.typography.font250.lineHeight as string;
      break;
    case SIZE.large:
      size = baseuiTheme.typography.font300.lineHeight as string;
      break;
  }

  return parseInt(size as string);
};

export const SelectHighZIndexOverrides = {
  Dropdown: {
    style: () => ({
      maxHeight: "250px",
      overflow: "auto",
    }),
  },
  Popover: {
    props: {
      overrides: {
        Body: {
          style: () => ({
            zIndex: "99999",
          }),
        },
      },
    },
  },
};

//Dynamically generate overrides for the Select component because the size prop which is needed to scale the icons is not accessible in all of the children components
const getSelectOverrides = (
  props: BaseSelectProps & {
    dataTest: string;
    negativeGroupKeys?: string[];
    dataAnalyticsId?: string;
  },
) => {
  const iconSize = getIconSize(props.size);

  // This is a workaround to force aria-label to be present on the input element regardess if the component is searchable or not.
  const iSearchable = props.searchable;
  const selectedOption = findSelectedOption(
    props.options as { id: string; label: string }[],
    props.value?.[0]?.id,
  );
  const ariaLabel = props["aria-label"];
  const ariaLabelPrefix = ariaLabel ? `${ariaLabel}, ` : "";
  const ariaLabelSuffix = selectedOption?.label
    ? `current selection is ${selectedOption.label}.`
    : "currently none selected.";

  // This aria label follows the same format as the one used in the baseui library when the component is searchable.
  const inputAriaLabel = `${ariaLabelPrefix}${ariaLabelSuffix}`;

  return {
    Input: {
      props: iSearchable
        ? {}
        : {
            "aria-hidden": false,
            "aria-readonly": "true",
            role: "group", // Announces as a group instead of a combobox
            "aria-haspopup": "listbox", // Still indicates it controls a listbox
            ...(ariaLabel ? { "aria-label": inputAriaLabel } : {}), // There are instances in which the label is handle by a FormControl componen so we want to avoid overwriting it
          },
    },
    Dropdown: {
      style: {
        maxHeight: "250px",
        overflow: "auto",
        borderTopLeftRadius: "0px",
        borderTopRightRadius: "0px",
        borderBottomLeftRadius: "0px",
        borderBottomRightRadius: "0px",
      },
      props: {
        "data-test": `${props.dataTest}-dropdown`,
      },
    },
    DropdownContainer: {
      style: {
        borderTopLeftRadius: "0px",
        borderTopRightRadius: "0px",
        borderBottomLeftRadius: "0px",
        borderBottomRightRadius: "0px",
      },
    },
    Placeholder: {
      style: ({ $theme }: { $theme: Theme }) => ({
        color: $theme.colors.contentTertiary,
      }),
    },
    DropdownListItem: {
      props: {
        "data-test": `${props.dataTest}-option`,
      },
      component: forwardRef(function DropdownListItem(
        rest: BaseSelectProps & {
          dataTest?: string | undefined;
          negativeGroupKeys?: string[] | undefined;
        },
        ref: ForwardedRef<HTMLUListElement>,
      ) {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        const { item, ...dropdownListItemProps } = rest;

        // eslint-disable-next-line react/prop-types
        const label = item?.[props?.labelKey ?? "label"];

        // eslint-disable-next-line react/prop-types
        if (props.negativeGroupKeys && props.negativeGroupKeys.includes(item?.__optgroup)) {
          // Note: Do not overwrite the id on this element, otherwise the ul element will not
          // be able to track the aria-activedescendent properly and screen readers will not
          // read out the list of options.
          return (
            <NegativeStyledDropdownListItem
              {...dropdownListItemProps}
              data-test-id={item?.id?.toString()}
              aria-label={label}
              ref={ref}
            >
              {label}
            </NegativeStyledDropdownListItem>
          );
        }

        return (
          // Note: Do not overwrite the id on this element, otherwise the ul element will not
          // be able to track the aria-activedescendent properly and screen readers will not
          // read out the list of options.
          <StyledDropdownListItem
            {...dropdownListItemProps}
            data-test-id={item?.id?.toString()}
            // typescript doesn't know what to do with the forwardedRef
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            ref={ref}
            aria-label={label}
          >
            {label}
          </StyledDropdownListItem>
        );
      }),
    },
    ControlContainer: {
      props: {
        "data-test": `${props.dataTest}-input`,
        "data-analytics-id": props.dataAnalyticsId,
      },
      style: ({
        $theme,
        $disabled,
        $isFocused,
        $positive,
        $error,
      }: {
        $theme: Theme;
        $disabled: boolean;
        $isFocused: boolean;
        $positive: boolean;
        $error: boolean;
      }) => {
        if (!$error && !$positive) {
          return {
            backgroundColor: $isFocused
              ? $theme.colors.mono100
              : $theme.colors.backgroundAlwaysLight,
            ...(!$isFocused && {
              borderTopColor: $theme.colors.mono600,
              borderBottomColor: $theme.colors.mono600,
              borderLeftColor: $theme.colors.mono600,
              borderRightColor: $theme.colors.mono600,
            }),
            ...($disabled && {
              borderTopColor: $theme.colors.mono500,
              borderBottomColor: $theme.colors.mono500,
              borderLeftColor: $theme.colors.mono500,
              borderRightColor: $theme.colors.mono500,
            }),
            borderTopLeftRadius: "0px",
            borderTopRightRadius: "0px",
            borderBottomLeftRadius: "0px",
            borderBottomRightRadius: "0px",
          };
        }
        if ($error) {
          return {
            color: $theme.colors.negative500,
            borderTopLeftRadius: "0px",
            borderTopRightRadius: "0px",
            borderBottomLeftRadius: "0px",
            borderBottomRightRadius: "0px",
          };
        }
        return {
          borderTopLeftRadius: "0px",
          borderTopRightRadius: "0px",
          borderBottomLeftRadius: "0px",
          borderBottomRightRadius: "0px",
        };
      },
    },
    SelectArrow: {
      component: function SelectIcon(props: CarbonIconProps & { overrides: Overrides }) {
        // We're removing aria-hidden from the props because it's not needed and it's creating an accessibility issue
        // https://www.w3.org/WAI/WCAG22/Understanding/name-role-value.html
        const { overrides, "aria-hidden": ariaHidden, ...restProps } = props;
        return <StyledSelectIcon {...restProps} focusable={false} />;
      },
    },
    SearchIconContainer: {
      component: function selectSearchIcon() {
        return (
          <StyledSearchIcon>
            <Search size={20} width={iconSize} height={iconSize} />
          </StyledSearchIcon>
        );
      },
    },
    ClearIcon: {
      props: {
        overrides: {
          Svg: {
            component: function selectClearIcon({
              children,
              size,
              viewBox,
              ...iconProps
            }: unknown) {
              return (
                <InputClearIcon
                  {...iconProps}
                  width={iconSize}
                  height={iconSize}
                  withMargin
                  //A little hack to get insert a title node for accessibility
                >
                  {children[0]}
                </InputClearIcon>
              );
            },
          },
        },
      },
    },
    Popover: {
      props: {
        overrides: {
          Body: {
            style: {
              zIndex: "99999",
            },
          },
        },
      },
    },
  };
};

type Option = { id: string; label: string };
type Options = Option[] | { [key: string]: Option[] } | undefined | null;

const findSelectedOption = (options: Options, id: string | undefined | null | number) => {
  if (!options || !id) {
    return null;
  }

  let flattenedOptions: Option[] = [];

  // Options can be an array or an object.
  // It's an object when we want the items to be grouped with a label. E.g. { "Group 1": [{ id: "1", label: "Option 1" }] }
  if (Array.isArray(options)) {
    flattenedOptions = options;
  } else {
    for (const key in options) {
      if (options[key]) {
        flattenedOptions = flattenedOptions.concat(options[key]);
      }
    }
  }

  const optionFound = flattenedOptions.find((option) => option.id === id);
  return optionFound;
};

const Select = ({
  overrides,
  clearable = false,
  searchable = false,
  dataTest = "select",
  dataAnalyticsId,
  negativeGroupKeys = [],
  error,
  ...props
}: SelectProps) => {
  return (
    <BaseSelect
      {...props}
      clearable={clearable}
      searchable={searchable}
      error={error}
      overrides={mergeOverrides(
        getSelectOverrides({ ...props, dataTest, negativeGroupKeys, dataAnalyticsId, searchable }),
        overrides as Overrides<
          SelectOverrides & {
            $disabled: boolean;
            $isFocused: boolean;
            $positive: boolean;
            $error: boolean;
          }
        >,
      )}
    />
  );
};

export default Select;
